/**
 * The imported world's ground, fetched once.
 *
 * **Its own request, deliberately not a field on `/places`.** That response is
 * re-asked every time fog moves and every time the party moves, because both
 * change which waypoints this caller may see. The grid changes only when a world
 * is imported, and it is the largest thing the map has to carry — so it is asked
 * for on the campaign id alone.
 *
 * **`reload` is the one thing that invalidates it, and an import must call it.**
 * The lines out of that same import ride on `/places`, which the importer already
 * reloads — so without this the Owner who has just imported a world watches its
 * rivers and roads appear over no ground at all until they reload the page, which
 * reads as a half-broken import rather than as a stale cache.
 *
 * Room-wide, so unlike `usePlaces` there is no per-viewer answer here: the
 * rivers and roads over this same ground are already served to everyone, and
 * fogging the ground beneath a visible river would look like a fault.
 *
 * A 404 is the ordinary case — most campaigns have no imported world — so it
 * resolves to `undefined` and the map simply draws no ground.
 */

import { useCallback, useEffect, useState } from 'react';

import { api } from '../api.js';
import type { TerrainGridView } from './terrain-palette.js';

export function useTerrainGrid(campaignId: string | undefined): {
  grid: TerrainGridView | undefined;
  reload: () => void;
} {
  const [grid, setGrid] = useState<TerrainGridView | undefined>(undefined);
  // Bumped by `reload`, on the same pattern as `usePlaces`: a counter rather
  // than a flag, so two imports in a row are two effects.
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    if (!campaignId) {
      setGrid(undefined);
      return;
    }
    let live = true;
    void api
      .get<{ grid: TerrainGridView }>(`/api/campaigns/${campaignId}/terrain`)
      .then((body) => {
        // A response landing after the component moved on would paint one
        // campaign's ground under another's map.
        if (!live) return;
        setGrid(isDrawable(body.grid) ? body.grid : undefined);
      })
      // 404 is "no world imported", which is most tables. Anything else is a
      // map that will not load, and must not take the table down with it.
      .catch(() => {
        if (live) setGrid(undefined);
      });
    return () => {
      live = false;
    };
  }, [campaignId, generation]);

  const reload = useCallback(() => setGeneration((n) => n + 1), []);
  return { grid, reload };
}

/**
 * Whether a response is a grid this can actually paint.
 *
 * Shape-checked rather than trusted, on the same reasoning as `hydrate` in the
 * repository: the import validates on the way in, but a row written by an older
 * version — or edited by hand — reaches here as whatever it is, and a `cells`
 * array shorter than its own dimensions would paint a smear rather than fail.
 */
function isDrawable(grid: TerrainGridView | undefined): grid is TerrainGridView {
  return (
    !!grid &&
    Number.isInteger(grid.width) &&
    Number.isInteger(grid.height) &&
    grid.width > 0 &&
    grid.height > 0 &&
    Array.isArray(grid.legend) &&
    Array.isArray(grid.cells) &&
    grid.cells.length === grid.width * grid.height
  );
}
